import { AnyAction, Dispatch } from 'redux'
import {
  getErrorMessages,
  RequestFailureMessage,
  updateAppAlert,
  startLoading,
  stopLoading,
  LoadingTypes,
  errorLogger,
} from '@labrav/common'
import { camelize } from 'casing'
import FileDownload from 'js-file-download'
import { isEmpty } from 'lodash'
import { AxiosError, AxiosResponse } from 'axios'

import {
  fetchAzureMeteringReport,
  fetchMeteringReport,
  fetchUsageData,
  getSearchedData,
  fetchAzureUsageData,
} from '../../api/marketplace/index'
import type { AppDispatch, RootState } from '../../store/types'
import {
  DateRange,
  UsageDataState,
  UsageFilterState,
  UsageMeteringActionTypes,
} from './types'
import { CloudMarketplace } from '../productsListing/types'

export { UsageMeteringActionTypes }

// `selectedInput` is the DB column/field name to filter on, coming from
// searchDropDownOptionMapping.meteringOptions ('buyer_company_name'). Maps it
// to the explicit query param fetchUsageData expects.
const getUsageMeteringSearchParams = (
  input?: string,
  selectedInput?: string
) => {
  if (isEmpty(input)) {
    return {}
  }
  switch (selectedInput) {
    case 'buyer_company_name':
      return { companyName: input }
    default:
      return {}
  }
}

export const getUsageData =
  ({
    pageSize,
    pageNumber,
    cloud,
    input,
    selectedInput,
    sectionName,
    accountId,
    cloudType,
  }: {
    pageSize?: number
    pageNumber?: number
    cloud?: string[]
    status?: string[]
    input?: string
    selectedInput?: string
    sectionName?: string
    accountId?: string
    cloudType: CloudMarketplace
  }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.USAGE_LOADING))
    try {
      const partnerId =
        accountId || getState().AccountData?.user?.partnerData?.partnerId || ''
      const { dateRangeFilter, status } = getState().usageData?.filters ?? {
        status: [],
        cloud: [],
        dateRangeFilter: {},
      }
      const finalStartDate = dateRangeFilter?.startDate
      const finalEndDate = dateRangeFilter?.endDate

      if (cloudType === 'AWS') {
        // Usage metering search now queries the marketplace service directly
        // (DB-backed) instead of going through the OpenSearch based search
        // service.
        const response = await fetchUsageData({
          partnerId,
          pageNumber,
          pageSize,
          cloud,
          status,
          finalStartDate,
          finalEndDate,
          ...getUsageMeteringSearchParams(input, selectedInput),
        })
        await dispatch(
          setUsageMeteringData(camelize(response.data) as UsageDataState)
        )
      } else if (isEmpty(input)) {
        const response = await fetchAzureUsageData({
          partnerId,
          pageNumber,
          pageSize,
          cloud,
          status,
          finalStartDate,
          finalEndDate,
        })
        await dispatch(
          setUsageMeteringData(camelize(response.data) as UsageDataState)
        )
      } else {
        let additionalParams = ''
        if (!isEmpty(status)) {
          additionalParams += `&status=${status?.join(',').toUpperCase()}`
        }
        if (!isEmpty(finalStartDate)) {
          additionalParams += `&from_date=${finalStartDate}`
        }
        if (!isEmpty(finalEndDate)) {
          additionalParams += `&to_date=${finalEndDate}`
        }

        const response = await getSearchedData({
          partnerId,
          input,
          selectedInput,
          pageSize,
          pageNumber,
          sectionName,
          additionalParams,
        })
        const responseData = response?.data as {
          pagination_object?: { current_page?: number }
          total_count?: number
          records: Array<{ _source: Record<string, unknown> & { id: string } }>
        }
        const numberOfPage = responseData?.pagination_object?.current_page
        const totalCount = responseData?.total_count
        const rows = responseData.records.map(recordItem => ({
          ...recordItem._source,
          labraMetering_id: recordItem._source.id,
        }))
        const data = {
          ...responseData,
          data: rows,
          metaData: {
            page_size: pageSize,
            page_number: numberOfPage,
            sort_by: 'created_at',
            sort_order: 'desc',
            total_count: totalCount,
          },
        }
        await dispatch(setUsageMeteringData(camelize(data) as UsageDataState))
      }
      await dispatch(
        updateAppAlert({
          message: '',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.USAGE_LOADING))
    }
  }

export const setUsageMeteringData = (usageData: UsageDataState) => {
  return {
    type: UsageMeteringActionTypes.SET_USAGE_METERING_DATA,
    payload: usageData,
  }
}

export const clearUsageMeteringData = () =>
  ({
    type: UsageMeteringActionTypes.CLEAR_USAGE_METERING_DATA,
  }) as unknown as AnyAction

export const getMeteringReportFile =
  (
    fromDate: string,
    toDate: string,
    cloudProductId: string,
    cloudProvider: string
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO))

    try {
      const partnerId =
        getState().AccountData?.user?.partnerData?.partnerId || ''
      const response = await fetchMeteringReport({
        partnerId,
        fromDate,
        toDate,
        cloudProductId,
      })

      const filename = `metering_history_${cloudProvider}_${cloudProductId}_${fromDate}_${toDate}.csv`
      FileDownload(response.data as string | Blob, filename)

      dispatch(
        updateAppAlert({
          message: 'Metering history downloaded.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO))
    }
  }

export const getAzureMeteringReportFile =
  (fromDate: string, toDate: string, cloudProductCode: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO))

    try {
      const partnerId =
        getState().AccountData?.user?.partnerData?.partnerId || ''
      const response = await fetchAzureMeteringReport({
        partnerId,
        fromDate,
        toDate,
        cloudProductCode,
      })

      const filename = `metering_history_AZURE_${cloudProductCode}_${fromDate}_${toDate}.csv`
      FileDownload(response.data as string | Blob, filename)

      dispatch(
        updateAppAlert({
          message: 'Metering history downloaded.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState: globalState as never })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO))
    }
  }

export const updateUsageDateRange =
  (dates: DateRange) => async (dispatch: AppDispatch) => {
    const { startDate, endDate } = dates
    await dispatch(setUsageDateRange({ startDate, endDate }))
  }

export const setUsageDateRange = (dates: DateRange) => ({
  type: UsageMeteringActionTypes.SET_USAGE_DATE_RANGE,
  payload: dates,
})

export const setFilters = (filters: UsageFilterState) => ({
  type: UsageMeteringActionTypes.SET_USAGE_FILTERS,
  payload: filters,
})

export const clearFilters = () => ({
  type: UsageMeteringActionTypes.CLEAR_USAGE_FILTERS,
})

export const setUsagePageNumber = (pageNumber: number) => ({
  type: UsageMeteringActionTypes.SET_PAGE_NUMBER,
  payload: pageNumber,
})
